import React from 'react';
import PropTypes from 'prop-types';
import prettierBytes from 'prettier-bytes';
import useClassNames from '@medflex/hooks/useClassNames';
import useIsExtraSmallScreen from '@medflex/hooks/useIsExtraSmallScreen';
import truncateIfTooLong from '@medflex/utils/truncateIfTooLong';
import Button from '../Button';
import { IconClose, IconExclamationOutline } from '../Icons';
import FileIcon from './FileIcon';

const FileItem = ({
  type,
  name,
  size,
  percentage,
  extension,
  small = false,
  className,
  hasError,
  onDelete,
  onClick,
}) => {
  const cn = useClassNames(
    () => [
      'relative',
      'bg-slate-50',
      'rounded-lg',
      'flex',
      'justify-between',
      'items-center',
      'w-full',
      !small ? ['py-6', 'pl-4', 'pr-8'] : ['py-1', 'px-2'],
    ],
    [className],
  );

  const progressCn = useClassNames(
    () => [
      'absolute',
      'left-0',
      'top-0',
      'bottom-0',
      'bg-slate-200',
      percentage && percentage < 100 ? 'rounded-l-lg' : 'rounded-lg',
    ],
    [percentage],
  );

  const displayName = useIsExtraSmallScreen() ? truncateIfTooLong(name, 20) : name;

  return (
    // eslint-disable-next-line
    <div className={cn} data-component="FileItem" onClick={onClick}>
      {!!percentage && (
        <div className={progressCn} style={{ width: `${percentage}%`, maxWidth: '100%' }} />
      )}
      <div className="flex items-center overflow-hidden z-0 flex-auto">
        <span className="inline-flex flex-shrink-0">
          {hasError ? (
            <IconExclamationOutline size={small ? '6' : '12'} className="text-red-600" />
          ) : (
            <FileIcon mime={type} size={small ? '6' : '12'} className="text-green-500 " />
          )}
        </span>
        <div className={`flex flex-col overflow-hidden justify-center ${small ? 'ml-2' : 'ml-4'}`}>
          <div className="font-semibold min-w-0 leading-normal text-sm">
            <div className="truncate">{displayName}</div>
          </div>
          {!small && (
            <p className="text-slate-500">
              {size && `${prettierBytes(size)} / `}
              {extension}
            </p>
          )}
        </div>
      </div>
      <Button
        size="xs"
        shape="circle"
        onClick={onDelete}
        title="Datei entfernen"
        className={`z-0 ml-2 flex-shrink-0 top-0 right-0 ${!small ? 'absolute top-0 right-0' : ''}`}
        kind="secondary:ghost"
      >
        <IconClose size="text" />
      </Button>
    </div>
  );
};

FileItem.propTypes = {
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  extension: PropTypes.string,
  size: PropTypes.number,
  percentage: PropTypes.number,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  small: PropTypes.bool,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  hasError: PropTypes.bool,
};

FileItem.defaultProps = {
  extension: '',
  small: false,
  onDelete: () => {},
  onClick: () => {},
  percentage: null,
  hasError: false,
};

export default FileItem;
